import { useMemo } from 'react';
import type { freeFormFilterValues, FreeFormHubFilterKeysType } from '../product-filters/types';
import { freeFormHubFilterKeys } from '../product-filters/types';
import type { StreamProduct } from '@igus/iglidur-free-form-hub-interfaces';
import { usMarketMaterials } from '@hub/bearing-hub/common/materials/useMaterials';
import type { UnitSystem } from '@igus/kopla-domain';
import { toMetricPressureMega, toMetricTemperature } from '@igus/kopla-domain';

export const useFilteredProductList = (
  productToFilter: StreamProduct[],
  filterList: freeFormFilterValues,
  unitSystem: UnitSystem
) => {
  const getOnlyUsMaterials = (products: StreamProduct[]) => {
    return products.filter(product => usMarketMaterials.includes(product.material.id));
  };

  const getFilteredByMaterial = (products: StreamProduct[], materialIds: string[]) => {
    if (!materialIds.length) {
      return products;
    }

    return products.filter(product => materialIds.includes(product.material.name));
  };

  const getFilteredByProductionMethod = (products: StreamProduct[], productionMethods: string[]) => {
    if (!productionMethods.length) {
      return products;
    }

    return products.filter(product => productionMethods.includes(product.material.procedure));
  };

  const getFilteredByMaterialProperty = (products: StreamProduct[], properties: freeFormFilterValues = {}) => {
    const { [freeFormHubFilterKeys.US_ONLY_MATERIALS]: _, ...filterKeys } = properties;
    const propertyFilterList = Object.keys(filterKeys).reduce(
      (acc: FreeFormHubFilterKeysType[], key: FreeFormHubFilterKeysType) => {
        if (filterKeys[key] === 'true') {
          acc.push(key);
        }
        return acc;
      },
      []
    );

    if (!propertyFilterList.length) {
      return products;
    }

    return products.filter(product =>
      propertyFilterList.every(property => {
        return Object.values(freeFormHubFilterKeys).some(value => value === property)
          ? product.material[property] === true
          : true;
      })
    );
  };

  const getFilteredByIntValue = (products: StreamProduct[], properties: freeFormFilterValues = {}) => {
    const expectedFilterPropertiesPositive: Partial<FreeFormHubFilterKeysType>[] = [
      freeFormHubFilterKeys.MAX_SURFACE_PRESSURE,
      freeFormHubFilterKeys.TEMPERATURE_RANGE_MAX,
    ];
    const expectedFilterPropertiesNegative: Partial<FreeFormHubFilterKeysType>[] = [
      freeFormHubFilterKeys.TEMPERATURE_RANGE_MIN,
    ];
    const propertyFilterList = Object.keys(properties).filter((key: FreeFormHubFilterKeysType) =>
      [...expectedFilterPropertiesPositive, ...expectedFilterPropertiesNegative].includes(key)
    );

    if (!propertyFilterList.length) {
      return products;
    }

    return products.filter(product =>
      propertyFilterList.every((property: Partial<FreeFormHubFilterKeysType>) => {
        const converterFunction =
          property === freeFormHubFilterKeys.MAX_SURFACE_PRESSURE ? toMetricPressureMega : toMetricTemperature;
        const compareNegative = expectedFilterPropertiesNegative.includes(property);
        const value = converterFunction(unitSystem, Number(properties[property]));
        return compareNegative
          ? Number(product.material[property]) <= value
          : Number(product.material[property]) >= value;
      })
    );
  };

  return useMemo(() => {
    let filteredProducts = productToFilter;
    if (filterList?.usOnlyMaterials) {
      filteredProducts = getOnlyUsMaterials(filteredProducts);
    }
    if (filterList?.material) {
      filteredProducts = getFilteredByMaterial(filteredProducts, filterList.material.split(','));
    }
    if (filterList?.productionMethod) {
      filteredProducts = getFilteredByProductionMethod(filteredProducts, filterList.productionMethod.split(','));
    }

    filteredProducts = getFilteredByMaterialProperty(filteredProducts, filterList);
    filteredProducts = getFilteredByIntValue(filteredProducts, filterList);

    return filteredProducts;
  }, [filterList, productToFilter, unitSystem]);
};
